import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  User as FirebaseUser,
  UserInfo,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as signOutFromFirebase,
  updateProfile,
} from 'firebase/auth';
import { JSX, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, UserCollection, UserMailCollection } from '../dto/user';
import { useFirebase } from '../hooks/firebase.hook';
import { removeNullFields } from '../util/util';

export interface AuthInterface {
  isLoggedIn: boolean;
  user?: User;
  isUserLoading: boolean;
  signUp: (name: string, mail: string, password: string) => Promise<void>;
  signIn: (mail: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  sendPasswordReset: (mail: string) => Promise<void>;
  resetPassword: (oobCode: string, newPassword: string) => Promise<void>;
  updateName: (name: string) => Promise<void>;
}

export function useAuthContext(): AuthInterface {
  return useContext(AuthContext);
}

export const AuthContext = createContext<AuthInterface>(undefined as unknown as AuthInterface);

export function AuthContextProvider(props: Readonly<PropsWithChildren>): JSX.Element {
  const { auth, setDocument, deleteDocument, getDocument } = useFirebase();

  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userDoc, setUserDoc] = useState<User>();

  useEffect(() => {
    onAuthStateChanged(auth, (u) => {
      setUserInfo(u ?? undefined);
      if (u) {
        getDocument<User>(UserCollection, u.uid)
          // heal users whose Firestore doc was never created (bug fixed)
          .then((doc) => (doc ? doc : updateUserData(u)))
          .then(setUserDoc)
          .finally(() => setIsLoading(false));
      } else {
        setUserDoc(undefined);
        setIsLoading(false);
      }
    });
  }, [auth]);

  // auth methods
  async function signUp(name: string, mail: string, password: string): Promise<void> {
    setIsLoading(true);
    await createUserWithEmailAndPassword(auth, mail, password)
      .then((credential) => updateProfileName(credential.user, name).then(() => credential.user))
      .catch(handleError);
  }

  async function signIn(mail: string, password: string): Promise<void> {
    setIsLoading(true);
    await signInWithEmailAndPassword(auth, mail, password)
      .then((credential) => updateUserData(credential.user))
      .catch(handleError);
  }

  async function signOut(): Promise<void> {
    setIsLoading(true);
    return signOutFromFirebase(auth).catch(handleError);
  }

  async function deleteAccount(password: string): Promise<void> {
    const authUser = auth.currentUser;
    const user = mapUser(userInfo);
    if (!authUser || !user) return;

    const credential = EmailAuthProvider.credential(user.mail, password);

    return reauthenticateWithCredential(authUser, credential)
      .then(() => deleteUser(user))
      .then(() => authUser.delete())
      .catch(handleError);
  }

  async function sendPasswordReset(mail: string): Promise<void> {
    return sendPasswordResetEmail(auth, mail);
  }

  async function resetPassword(oobCode: string, newPassword: string): Promise<void> {
    await confirmPasswordReset(auth, oobCode, newPassword).catch(handleError);
  }

  async function updateName(name: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) return;

    return updateProfileName(user, name);
  }

  // helper methods
  async function updateProfileName(user: FirebaseUser, name: string): Promise<void> {
    await updateProfile(user, { displayName: name })
      .then(() => user.reload())
      .then(() => setUserInfo({ ...user }))
      .then(() => updateUserData(user))
      .catch(handleError);
  }

  function handleError(e: FirebaseError): never {
    setIsLoading(false);

    const firebaseError = /\(([^)]+)\)/.exec(e.message)?.[1];
    if (firebaseError) {
      throw new Error(firebaseError);
    } else {
      throw e;
    }
  }

  function mapUser(user: UserInfo | undefined): User | undefined {
    if (!user?.email || !user?.displayName) return;

    return removeNullFields({ id: user.uid, name: user.displayName, mail: user.email, role: userDoc?.role });
  }

  // user firestore methods
  async function updateUserData(info: UserInfo): Promise<User | undefined> {
    const user = mapUser(info);
    if (!user) return;

    await setDocument(UserMailCollection, { id: user.id }, user.mail);
    return setDocument(UserCollection, user, user.id, true);
  }

  async function deleteUser(user: User): Promise<void> {
    await deleteDocument(UserMailCollection, user.mail);
    return deleteDocument(UserCollection, user.id);
  }

  // context
  const context: AuthInterface = useMemo(
    () => ({
      isLoggedIn: userInfo?.displayName != null,
      isUserLoading: isLoading || (userInfo != null && userInfo.displayName == null),
      user: mapUser(userInfo),
      signUp,
      signIn,
      signOut,
      deleteAccount,
      sendPasswordReset,
      resetPassword,
      updateName,
    }),
    [auth, setDocument, userInfo, userDoc, isLoading],
  );

  return <AuthContext.Provider value={context}>{props.children}</AuthContext.Provider>;
}
